
import React, { useState } from 'react';
import { MapPin, Package, Clock, Navigation, List, Map } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { mockOrders } from '@/api/mockData';

const Tracking = () => {
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'map'

  const filteredOrders = mockOrders.filter(order => 
    order.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.recipient.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'Đã giao':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'Đang giao':
      case 'Đang luân chuyển':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'Chờ xử lý':
      case 'Mới tạo':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      default: 
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-foreground">Theo dõi đơn hàng</h1>
        <div className="flex space-x-2">
          <Button
            variant={viewMode === 'list' ? 'default' : 'outline'}
            onClick={() => setViewMode('list')}
          >
            <List className="h-4 w-4 mr-2" />
            Danh sách
          </Button>
          <Button
            variant={viewMode === 'map' ? 'default' : 'outline'}
            onClick={() => setViewMode('map')}
          >
            <Map className="h-4 w-4 mr-2" />
            Bản đồ
          </Button>
        </div>
      </div>

      <div className="flex space-x-2">
        <Input
          placeholder="Tìm kiếm theo mã đơn hoặc tên người nhận..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="max-w-md"
        />
      </div>

      {viewMode === 'list' ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Danh sách đơn hàng */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Package className="h-5 w-5 mr-2 text-primary" />
                Danh sách đơn hàng
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {filteredOrders.map((order) => (
                  <div
                    key={order.id}
                    className={`p-4 border rounded-lg cursor-pointer transition-all duration-200 hover:shadow-md ${
                      selectedOrders.includes(order.id) ? 'border-primary bg-primary/5' : 'border-border'
                    }`}
                    onClick={() => {
                      setSelectedOrders(prev => 
                        prev.includes(order.id) 
                          ? prev.filter(id => id !== order.id)
                          : [...prev, order.id]
                      );
                    }}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div className="bg-primary/10 p-2 rounded-lg">
                          <Package className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-lg">{order.id}</h3>
                            {order.is3PL && (
                              <Badge variant="outline" className="text-xs">3PL</Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {new Date(order.timestamp).toLocaleString('vi-VN')}
                          </p>
                        </div>
                      </div>
                      <Badge className={getStatusColor(order.status)}>
                        {order.status}
                      </Badge>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div className="space-y-2">
                        <div className="flex items-center">
                          <span className="font-medium">Người nhận:</span>
                          <span className="ml-2">{order.recipient}</span>
                        </div>
                        <div className="flex items-center">
                          <MapPin className="h-4 w-4 mr-2 text-muted-foreground" />
                          <span className="truncate">{order.province}</span>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center">
                          <span className="font-medium">COD:</span>
                          <span className="ml-2 text-primary">{order.codAmount.toLocaleString()}đ</span>
                        </div>
                        <div className="flex items-center">
                          <span className="font-medium">Dịch vụ:</span>
                          <span className="ml-2">{order.service}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Chi tiết theo dõi */}
          <Card>
            <CardHeader>
              <CardTitle>Lịch trình theo dõi</CardTitle>
            </CardHeader>
            <CardContent>
              {selectedOrders.length === 0 ? (
                <div className="text-center text-muted-foreground py-8">
                  <Navigation className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                  <p>Chọn đơn hàng để xem lịch trình</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {selectedOrders.map(orderId => {
                    const order = filteredOrders.find(o => o.id === orderId);
                    if (!order || !order.trackingHistory) return null;
                    
                    return (
                      <div key={orderId} className="space-y-4">
                        <h4 className="font-semibold text-primary">{order.id}</h4>
                        <div className="space-y-3">
                          {order.trackingHistory.map((event, index) => (
                            <div key={index} className="flex items-start space-x-3">
                              <div className="flex flex-col items-center">
                                <div className={`w-3 h-3 rounded-full ${
                                  index === 0 ? 'bg-primary' : 'bg-muted-foreground'
                                }`} />
                                {index < order.trackingHistory.length - 1 && (
                                  <div className="w-px h-8 bg-border mt-2" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium">{event.status}</p>
                                <p className="text-sm text-muted-foreground">{event.location}</p>
                                <p className="text-xs text-muted-foreground">
                                  {new Date(event.time).toLocaleString('vi-VN')}
                                </p>
                                {event.description && (
                                  <p className="text-sm text-muted-foreground mt-1">{event.description}</p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Map className="h-5 w-5 mr-2 text-primary" />
              Bản đồ theo dõi
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-96 bg-muted rounded-lg flex items-center justify-center">
              <div className="text-center text-muted-foreground">
                <Map className="h-16 w-16 mx-auto mb-4" />
                <p className="text-lg">Tính năng bản đồ đang được phát triển</p>
                <p className="text-sm">Sẽ hiển thị vị trí thời gian thực của các đơn hàng</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Tracking;
